import { Injectable, signal } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { SidebarDTO } from '@models/sidebar.model';
import { HttpClient } from '@angular/common/http';
import { AppConfig } from '@env/app-config';

@Injectable({
  providedIn: 'root'
})
export class SidebarService {
  private isExpandedSubject = new BehaviorSubject<boolean>(true);
  private isMobileOpenSubject = new BehaviorSubject<boolean>(false);
  private isHoveredSubject = new BehaviorSubject<boolean>(false);
  private expandForUrlSubject = new Subject<string>();
  private currentBreakpointSubject = new BehaviorSubject<'mobile' | 'desktop'>(
    window.innerWidth >= 1280 ? 'desktop' : 'mobile'
  );

  isExpanded$ = this.isExpandedSubject.asObservable();
  isMobileOpen$ = this.isMobileOpenSubject.asObservable();
  isHovered$ = this.isHoveredSubject.asObservable();
  expandForUrl$ = this.expandForUrlSubject.asObservable();
  currentBreakpoint$ = this.currentBreakpointSubject.asObservable();

  /**
   * Signal para controlar si el menú de aplicaciones del header está abierto
   * Cuando está abierto, el sidebar se ajusta para no ser superpuesto
   */
  headerMenuOpen = signal<boolean>(false);

  private baseUrl = AppConfig.apiUrl;
  private apiUrl = `${this.baseUrl}/api/permission/sidebar`;
  constructor(private http: HttpClient) {}

  setExpanded(val: boolean) {
    this.isExpandedSubject.next(val);
  }

  toggleExpanded() {
    this.isExpandedSubject.next(!this.isExpandedSubject.value);
  }

  setMobileOpen(val: boolean) {
    this.isMobileOpenSubject.next(val);
  }

  toggleMobileOpen() {
    this.isMobileOpenSubject.next(!this.isMobileOpenSubject.value);
  }

  setHovered(val: boolean) {
    this.isHoveredSubject.next(val);
  }

  getSidebarModules(): Observable<SidebarDTO> {
    return this.http.get<SidebarDTO>(`${this.apiUrl}/modules/list`);
  }

  updateCategoryOrder(categoryId: number, groupId: number, order: number): Observable<any> {
    return this.http.patch(`${this.apiUrl}/modules/category/${categoryId}/order`, {
      module_groups_id: groupId,
      order
    });
  }

  updateModuleOrder(moduleId: number, categoryId: number, order: number): Observable<any> {
    return this.http.patch(`${this.apiUrl}/modules/module/${moduleId}/order`, {
      module_categories_id: categoryId,
      order
    });
  }

  updateCategoryStatus(categoryId: number, status: number): Observable<any> {
    return this.http.patch(`${this.apiUrl}/modules/category/${categoryId}/status`, { status });
  }

  updateModuleStatus(moduleId: number, status: number): Observable<any> {
    return this.http.patch(`${this.apiUrl}/modules/module/${moduleId}/status`, { status });
  }

  updateGroupStatus(groupId: number, status: number): Observable<any> {
    return this.http.patch(`${this.apiUrl}/modules/group/${groupId}/status`, { status });
  }
  /**
   * Emite una URL para forzar la expansión del sidebar en esa categoría
   */
  expandCategoryForUrl(url: string) {
    this.expandForUrlSubject.next(url);
  }

  /**
   * Establece el estado del menú del header (abierto/cerrado)
   * @param isOpen - true si el menú está abierto, false si está cerrado
   */
  setHeaderMenuOpen(isOpen: boolean) {
    this.headerMenuOpen.set(isOpen);
  }

  /**
   * Alterna el estado del menú del header
   */
  toggleHeaderMenu() {
    this.headerMenuOpen.update(current => !current);
  }

  /**
   * Actualiza el breakpoint actual y sincroniza estados
   * @param width - Ancho actual de la ventana
   */
  updateBreakpoint(width: number) {
    const newBreakpoint = width >= 1280 ? 'desktop' : 'mobile';
    const previousBreakpoint = this.currentBreakpointSubject.value;

    if (newBreakpoint !== previousBreakpoint) {
      this.currentBreakpointSubject.next(newBreakpoint);

      // De móvil a desktop: resetear estados para consistencia
      if (newBreakpoint === 'desktop') {
        this.isMobileOpenSubject.next(false);
        this.isHoveredSubject.next(false);
        this.isExpandedSubject.next(true); // Expandido por defecto en desktop
      }

      // De desktop a móvil: resetear estados
      if (newBreakpoint === 'mobile') {
        this.isMobileOpenSubject.next(false);
        this.isHoveredSubject.next(false);
        // isExpanded se mantiene pero no se usa en móvil
      }
    }
  }

  /**
   * Obtiene el breakpoint actual
   */
  getCurrentBreakpoint(): 'mobile' | 'desktop' {
    return this.currentBreakpointSubject.value;
  }
}
